import { useEffect } from "react";
import { Form } from "../../../system/useForm";
import FormYesNo from "../../../Components/FormYesNo";
import { Box, Grid, Typography } from "@mui/material";
import FormChoices from "../../../Components/FormChoices";
import FormDateTimePicker from "../../../Components/FormDateTimePicker";
import AssessmentRadioChoice from "./AssessmentRadioChoice";
import { DeliveryType, VehicleAddresses } from "../../../system/Domain";
import FormText from "../../../Components/FormText";
import { EditMode } from "./ReturnOrdersForm";
import useCompanies from "../../../system/useCompanies";
import AssessorCompanyChoice from "./AssessorCompanyChoice";
import useCompounds from "../../../system/useCompounds";

interface Props {
	form: Form;
	addresses: VehicleAddresses;
	editMode: EditMode;
}

const StepAssessment = (props: Props) => {
	const [companies] = useCompanies();
	const [compounds] = useCompounds({ includeAmazonCompounds: false });
	const entryType = props.form.getValue("delivery.deliveryType") ?? "";
	const isCustomerDelivery = entryType === DeliveryType.CustomerDelivery;
	const changingAssessmentInstructionsForbidden = props.editMode === "AssessmentPreProcessed";

	useEffect(() => {
		if (isCustomerDelivery) {
			props.form.setValue("doAssessment", true);
			props.form.setValue("assessment.assessmentType", "Minderwertsgutachten");
			props.form.setValue("assessment.assessmentLocation", "DeliveryAddress");
			props.form.setValue(
				"assessment.appointment.notBefore",
				props.form.getValue("delivery.appointment.notBefore")
			);
		}
	}, [entryType]);

	if (!companies) return <></>;

	return (
		<Box mb={2} mt={2}>
			<Grid container spacing={4}>
				<FormYesNo
					name="doAssessment"
					label="Soll ein Gutachten erstellt werden?"
					form={props.form}
					options={{
						required: true
					}}
					disabled={isCustomerDelivery || changingAssessmentInstructionsForbidden}
				/>
				{props.form.getValue("doAssessment") === true && (
					<>
						<FormChoices
							name="assessment.assessmentType"
							label="Welcher Gutachtentyp?"
							form={props.form}
							choices={{
								Restwertgutachten: "R1 - Restwertgutachten",
								Minderwertsgutachten: "R2 - Minderwertsgutachten"
							}}
							options={{ required: true }}
							disabled={isCustomerDelivery || changingAssessmentInstructionsForbidden}
						/>
						{props.form.getValue("assessment.assessmentType") && (
							<>
								<Grid item xs={12}>
									<AssessmentRadioChoice
										name="assessment.assessmentLocation"
										form={props.form}
										isRkv={
											false
										} /*this (!=false) leads to confusion, and because there are no stops, all assessments are done on either collection or delivery stations, this is intentionally disabled*/
										aldAddressData={props.addresses}
										required
										disabled={isCustomerDelivery || changingAssessmentInstructionsForbidden}
									/>
								</Grid>
								{props.form.getValue("assessment.assessmentLocation") && (
									<Grid item xs={12}>
										{companies && compounds && (
											<AssessorCompanyChoice
												form={props.form}
												companies={companies}
												name="assessment.company.id"
												compounds={compounds}
												assessmentLocation="assessment.assessmentLocation"
												deliveryPrefix="delivery."
												collectionPrefix="collection."
												assessmentType="assessment.assessmentType"
											/>
										)}
										<Box mb={2} sx={{ width: "50%" }}>
											<FormText
												name="assessment.assessmentComment"
												form={props.form}
												label="Bemerkung zum Gutachten"
												type="text"
												inline
												fullWidth
												readOnly={changingAssessmentInstructionsForbidden}
											/>
										</Box>
										<Typography variant="h6" gutterBottom>
											Wunschtermin
										</Typography>
										<Grid container spacing={3}>
											<Grid item xs={12} md={6}>
												<FormDateTimePicker
													name="assessment.appointment.notBefore"
													form={props.form}
													label="ab dem"
													margin="normal"
													variant="date"
													format="DD.MM.YYYY"
													disablePast
													disabled={changingAssessmentInstructionsForbidden}
												/>
											</Grid>
											<Grid item xs={12} sm={6} md={3}>
												<FormDateTimePicker
													name="assessment.appointment.from"
													form={props.form}
													label="Zeit von"
													variant="time"
													ampm={false}
													disabled={changingAssessmentInstructionsForbidden}
												/>
											</Grid>
											<Grid item xs={12} sm={6} md={3}>
												<FormDateTimePicker
													name="assessment.appointment.to"
													form={props.form}
													label="bis"
													variant="time"
													ampm={false}
													disabled={changingAssessmentInstructionsForbidden}
												/>
											</Grid>
										</Grid>
									</Grid>
								)}
							</>
						)}
					</>
				)}
			</Grid>
		</Box>
	);
};

export default StepAssessment;
